import React, { useState, useRef, useCallback } from 'react';
import axios from 'axios';
import { Form, FormGroup, FormControl, Button, FormLabel, Row } from 'react-bootstrap';
import { ToastContainer, toast } from 'react-toastify';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowRight } from '@fortawesome/free-solid-svg-icons';

import useToken from '../hooks/useToken';
import Comments from './Comments';
import 'react-toastify/dist/ReactToastify.css';

const LeaveReply = ({ id: postId }) => {
    const { token } = useToken();
    const siteUrl = process.env.REACT_APP_API_SITE_URL;
    const targetRef = useRef(null);

    const [formData, setFormData] = useState({
        name: '',
        email: '',
        comment: '',
        url: '',
    });

    const [parent, setParent] = useState(0);
    const [errors, setErrors] = useState({});

    const handleInputChange = useCallback(({ target: { name, value } }) => {
        setFormData((prev) => ({ ...prev, [name]: value }));
    }, []);

    const handleKeyUp = useCallback(({ target: { name, value } }) => {
        setErrors((prev) => {
            const updatedErrors = { ...prev };
            if (!value.trim()) {
                updatedErrors[name] = `${capitalize(name)} is required`;
            } else if (name === 'email' && !validateEmail(value)) {
                updatedErrors.email = 'A valid email address is required';
            } else {
                delete updatedErrors[name];
            }
            return updatedErrors;
        });
    }, []);

    const capitalize = (str) => str.charAt(0).toUpperCase() + str.slice(1);

    const validateEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

    const validateForm = () => {
        const newErrors = {};
        let isValid = true;

        if (!formData.name.trim()) {
            newErrors.name = 'Name is required';
            isValid = false;
        }

        if (!formData.email.trim()) {
            newErrors.email = 'Email is required';
            isValid = false;
        } else if (!validateEmail(formData.email)) {
            newErrors.email = 'A valid email address is required';
            isValid = false;
        }

        if (!formData.comment.trim()) {
            newErrors.comment = 'Comment is required';
            isValid = false;
        }

        setErrors(newErrors);
        return isValid;
    };

    const resetForm = () => {
        setFormData({ name: '', email: '', comment: '', url: '' });
        setParent(0);
    };

    const showToast = (type, message) => {
        toast[type](message, {
            position: 'bottom-left',
            autoClose: 5000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
            theme: 'light',
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validateForm()) return;

        const payload = {
            post_id: postId,
            parent,
            content: formData.comment,
            author_name: formData.name,
            author_email: formData.email,
        };

        try {
            const response = await axios.post(
                `${siteUrl}/wp-json/api/v2/content/posts/comments`,
                payload,
                {
                headers: {
                    'Content-Type': 'application/json; charset=UTF-8',
                    Authorization: `Bearer ${token}`,
                },
                }
            );

            const { status, message } = response.data;
            status === 'success' ? showToast('success', message) : showToast('error', message);
            resetForm();
        } catch (error) {
            console.error('Failed to submit comment:', error);
            showToast('error', 'Something went wrong while submitting your comment.');
        }
    };

    return (
        <>
        <Comments id={postId} updateVariable={setParent} targetRef={targetRef} />
        <div className="leave_reply_wrapper" ref={targetRef}>
            <h3 className="fw-500 text-6 text-dark">Leave a Reply</h3>
            <p className="text-muted mb-5">
                Your email address will not be published. Required fields are marked{' '}
                <span className="required">*</span>
            </p>
            <Form className="form-border" onSubmit={handleSubmit} noValidate>
                <Row>
                    <div className="col-lg-12 mb-3 wow fadeInUp">
                    <FormGroup>
                        <FormLabel htmlFor="comment">Comment<span className="required">*</span></FormLabel>
                        <FormControl
                            as="textarea"
                            name="comment"
                            rows={3}
                            value={formData.comment}
                            onChange={handleInputChange}
                            onKeyUp={handleKeyUp}
                        />
                        {errors.comment && <p className="text-danger">{errors.comment}</p>}
                    </FormGroup>
                    </div>
                </Row>

                <Row>
                    <div className="col-lg-6 mb-3 wow fadeInUp">
                        <FormGroup>
                            <FormLabel htmlFor="name">Name<span className="required">*</span></FormLabel>
                            <FormControl
                                type="text"
                                name="name"
                                value={formData.name}
                                onChange={handleInputChange}
                                onKeyUp={handleKeyUp}
                            />
                            {errors.name && <p className="text-danger">{errors.name}</p>}
                        </FormGroup>
                    </div>

                    <div className="col-lg-6 mb-3 wow fadeInUp">
                        <FormGroup>
                            <FormLabel htmlFor="url">URL</FormLabel>
                            <FormControl
                                type="text"
                                name="url"
                                value={formData.url}
                                onChange={handleInputChange}
                                onKeyUp={handleKeyUp}
                            />
                        </FormGroup>
                    </div>
                </Row>

                <Row>
                    <div className="col-lg-12 mb-3 wow fadeInUp">
                        <FormGroup>
                            <FormLabel htmlFor="email">Email Address<span className="required">*</span></FormLabel>
                            <FormControl
                                type="email"
                                name="email"
                                value={formData.email}
                                onChange={handleInputChange}
                                onKeyUp={handleKeyUp}
                            />
                                {errors.email && <p className="text-danger">{errors.email}</p>}
                        </FormGroup>
                    </div>
                </Row>

                <Row>
                    <div className="col-lg-12 mt-3 wow fadeInUp">
                        <Button type="submit" className="btn btn-dark rounded-2 cbtn position-relative">
                            Post Comment <FontAwesomeIcon icon={faArrowRight} className="ms-3" />
                        </Button>
                    </div>
                </Row>
            </Form>
            <ToastContainer />
        </div>
        </>
    );
};

export default LeaveReply;
